import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import { EmailConsentService } from '@/services/emailConsentService';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import { appHref } from '@/utils/appHref';

/**
 * /unsubscribe — the page behind every "unsubscribe" link in account email.
 *
 * History (#284): this page used to write `profile.emailConsent` via a
 * client-side updateDoc. No sender reads that field, and the write required
 * the visitor to be signed in as that uid, which unsubscribe clickers never
 * are. It showed success while changing nothing.
 *
 * Now: one confirm click posts to the backend. Accounts hit the
 * `confirmUnsubscribe` callable (writes `consent.marketingEmailConsent` — the
 * field the sending gate reads); diagnostic leads (`?lead=`) hit
 * `unsubscribeLead` (flips `lead.unsubscribed`, which the lead-nurture drip
 * skips on). Both are unauthenticated by design, rate limited, and only ever
 * turn mail OFF.
 *
 * BOTH paths require an explicit button click — never an auto-submit on mount.
 * Email clients and security scanners prefetch links (and some execute page
 * JS); a page that unsubscribed on load would silently opt people out when a
 * crawler touched the URL. The opt-out happens only in handleConfirm below.
 */
const UnsubscribePage: React.FC = () => {
  const location = useLocation();
  const { currentUser } = useEnhancedAuth();
  const [status, setStatus] = useState<'confirm' | 'loading' | 'success' | 'error'>('loading');
  const [userId, setUserId] = useState<string>('');
  const [leadId, setLeadId] = useState<string>('');

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const lead = urlParams.get('lead');
    const uid = urlParams.get('user_id') || currentUser?.uid || '';

    // Diagnostic leads have no account; identify them by the lead token and let
    // the same confirm button below do the opt-out. No auto-submit (crawler safety).
    if (lead) {
      setLeadId(lead);
      setStatus('confirm');
      return;
    }

    if (!uid) {
      setStatus('error');
      return;
    }

    setUserId(uid);
    setStatus('confirm');
  }, [location.search, currentUser]);

  const handleConfirm = async () => {
    setStatus('loading');
    try {
      if (leadId) {
        await EmailConsentService.unsubscribeLead(leadId);
      } else {
        await httpsCallable(functions, 'confirmUnsubscribe')({ userId });
      }
      setStatus('success');
    } catch (_error) {
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-neutral-600">Processing your request...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-3xl border-2 border-neutral-200 shadow-ledge p-6">
        <div className="text-center">
          {status === 'confirm' && (
            <>
              <h2 className="font-display text-xl font-semibold text-neutral-900">Unsubscribe from our emails?</h2>
              <p className="mt-2 text-neutral-600">
                You will stop receiving Pam's letters, study tips, and product news.
                Account emails like purchase receipts and password resets are not affected.
              </p>
              <button
                onClick={handleConfirm}
                className="mt-6 w-full rounded-2xl bg-primary-600 px-4 py-3 font-bold text-white transition-colors hover:bg-primary-700"
              >
                Yes, unsubscribe me
              </button>
              <a href={appHref('/')} className="mt-3 inline-block text-sm font-bold text-neutral-500 hover:text-neutral-700">
                Keep my emails
              </a>
            </>
          )}

          {status === 'success' && (
            <>
              <h2 className="font-display text-xl font-semibold text-green-600">You're unsubscribed</h2>
              <p className="mt-2 text-neutral-600">
                No more marketing email from us. Account emails like receipts and
                password resets will still arrive when you need them.
              </p>
              <p className="mt-4 text-sm text-neutral-500">
                Changed your mind? You can turn emails back on any time in your
                account settings.
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <h2 className="font-display text-xl font-semibold text-red-600">Something went wrong</h2>
              <p className="mt-2 text-neutral-600">
                We could not process this link. Please try again, or email{' '}
                <a href="mailto:support@socialworktestprepacademy.com" className="font-bold text-primary-600">
                  support@socialworktestprepacademy.com
                </a>{' '}
                and we will take care of it by hand.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;
